import { useMemo } from "react";
import { orderInvoiceSummaryService as defaultService } from "../services/orderInvoiceSummaryService";
import { useGetOrderInvoiceSummary } from "./useGetOrderInvoiceSummary";

// ─── Pure Logic ───────────────────────────────────────────────────────────────

export const buildSummaryParams = (userId, companyInvoiceId) => ({
  userId,
  companyInvoiceId,
});

export const deriveInvoiceSummaryState = invoice => ({
  hasData: invoice !== null,
  hasPlaces: (invoice?.places?.length ?? 0) > 0,
});

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Orchestrating hook for the invoice detail page.
 *
 * Builds a stable params object and delegates fetching to
 * useGetOrderInvoiceSummary. Re-fetches automatically when
 * userId or companyInvoiceId changes.
 *
 * `service` is injected for testability.
 */
export function useOrderInvoiceSummary(
  userId,
  companyInvoiceId,
  service = defaultService,
) {
  const params = useMemo(
    () => buildSummaryParams(userId, companyInvoiceId),
    [userId, companyInvoiceId],
  );

  const { status, invoice, error, refresh } = useGetOrderInvoiceSummary(
    params,
    service,
  );

  const derived = useMemo(() => deriveInvoiceSummaryState(invoice), [invoice]);

  return {
    status,
    invoice,
    error,
    hasData: derived.hasData,
    hasPlaces: derived.hasPlaces,
    refresh,
  };
}
